import { Config } from "@app/config";
import { faPlus as fasPlus } from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatText } from "@shared/ui/format";
import autobind from "autobind-decorator";
import type React from "react";
import type { ReactNode } from "react";
import { ListGroup, ListGroupItem } from "react-bootstrap";
import type { FieldRenderProps } from "react-final-form";
import { ButtonLegacy } from "../../ButtonLegacy";
import type { FormItemProps, FormItemState } from "../../FormItem";
import { FormItem } from "../../FormItem";
import type { FormIncomeSimpleData } from "./FormIncomeItem";
import { SimpleIncomeItem } from "./FormIncomeItem";
import { SimpleIncomeModal } from "./SimpleIncomeModal";
import "./style.scss";

export interface FormIncomeSimpleProps extends FormItemProps<FormIncomeSimpleData[]> {
	size?: "sm";
	apiAddress: string;
	withReceiptAmount?: boolean;
	withIncomeStart?: boolean;
	value?: FormIncomeSimpleData[];
	onChange?: (incomeList: FormIncomeSimpleData[]) => void;
	buttonVariant?: string;
}

interface State extends FormItemState {
	incomeList: FormIncomeSimpleData[];
	showModal: boolean;
	editKey?: number;
}

export class FormIncomeSimple extends FormItem<FormIncomeSimpleData[], FormIncomeSimpleProps, State> {
	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			incomeList: this.props.value || [],
			showModal: false,
			editKey: undefined,
		};
	}

	protected renderField(fieldProps: FieldRenderProps<FormIncomeSimpleData[]>): ReactNode {
		const {
			disabled,
			readOnly,
			apiAddress,
			tabIndex,
			withReceiptAmount,
			size,
			withIncomeStart,
			buttonVariant = "success",
		} = this.props;
		const { editKey, showModal } = this.state;
		const {
			input: { value: incomeList = [], name },
		} = fieldProps;

		const initialValues: FormIncomeSimpleData = incomeList[editKey] || {
			incomeType: undefined,
			incomeTypeName: "",
			amount: undefined,
			receiptAmount: undefined,
		};

		return (
			<>
				<ListGroup className="mb-3 mt-0 text-body income-list">
					<div className="items">
						{incomeList.map((income, key) => (
							<SimpleIncomeItem
								key={key}
								size={size}
								onEdit={() => {
									this.handleEditItem(fieldProps, key);
								}}
								onDelete={() => {
									this.handleDeleteItem(fieldProps, key);
								}}
								disabled={disabled || readOnly}
								withReceiptAmount={withReceiptAmount}
								withIncomeStart={withIncomeStart}
								{...income}
							/>
						))}
					</div>
					{this.renderTotal(incomeList)}
				</ListGroup>
				<div className="text-center">
					<ButtonLegacy
						data-name={name}
						variant={buttonVariant}
						icon={<FontAwesomeIcon icon={fasPlus} />}
						tabIndex={tabIndex}
						disabled={disabled || readOnly}
						onClick={this.handleAddIncomeClick}
						onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
							this.handleFocus(event, fieldProps);
						}}
						onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
							this.handleBlur(event, fieldProps);
						}}
					>
						Jövedelem hozzáadása
					</ButtonLegacy>
				</div>
				<SimpleIncomeModal
					apiAddress={apiAddress}
					show={showModal}
					withReceiptAmount={withReceiptAmount}
					withIncomeStart={withIncomeStart}
					initialValues={initialValues}
					onSave={(income: FormIncomeSimpleData) => {
						this.handleModalSave(fieldProps, income, editKey);
					}}
					onHide={() => {
						this.handleModalHide(fieldProps);
					}}
				/>
			</>
		);
	}

	protected renderTotal(incomeList: FormIncomeSimpleData[]): ReactNode {
		const { withReceiptAmount } = this.props;
		const totalAmount = this.getTotalAmount(incomeList);
		const receiptAmount = this.getTotalReceiptAmount(incomeList);

		if (totalAmount === 0) {
			return (
				<ListGroupItem className="text-center" variant="warning">
					Még nem nem adtál meg jövedelmet!
				</ListGroupItem>
			);
		}

		if (withReceiptAmount) {
			return (
				<ListGroup.Item variant="success" className="d-flex text-dark p-3">
					<div>
						<div className="h5 mb-1">Összesen:</div>
						<div className="h6 mb-0">Bankba érkeztetett:</div>
					</div>
					<div className="ml-auto text-right">
						<strong className="h5 mb-1">{formatText(totalAmount, "ft")}</strong>
						<strong className="h6 mb-0">{formatText(receiptAmount, "ft")}</strong>
					</div>
				</ListGroup.Item>
			);
		}

		return (
			<ListGroup.Item variant="success" className="text-dark p-3">
				<div className="d-flex">
					<div>Összesen: </div>
					<div className="ml-auto">
						<strong>{formatText(totalAmount, "ft")}</strong>
					</div>
				</div>
				{withReceiptAmount && (
					<small className="d-flex">
						<div>Bankhoz érkezhet: </div>
						<div className="ml-auto">
							<strong>{formatText(receiptAmount, "ft")}</strong>
						</div>
					</small>
				)}
			</ListGroup.Item>
		);
	}

	@autobind
	protected handleEditItem(_fieldProps: FieldRenderProps<FormIncomeSimpleData[]>, key: number): void {
		this.setState({
			showModal: true,
			editKey: key,
		});
	}

	@autobind
	protected handleDeleteItem(fieldProps: FieldRenderProps<FormIncomeSimpleData[]>, key: number): void {
		const {
			input: { value: incomeList = [] },
		} = fieldProps;

		const newIncomeList = [...incomeList];
		newIncomeList.splice(key, 1);

		this.handleChange(newIncomeList, fieldProps);
		this.handleBlur(undefined, fieldProps);
	}
	@autobind
	protected handleAddIncomeClick(): void {
		this.setState({
			showModal: true,
		});
	}

	@autobind
	protected handleModalHide(fieldProps: FieldRenderProps<FormIncomeSimpleData[]>): void {
		this.setState({
			showModal: false,
			editKey: undefined,
		});

		this.handleBlur(undefined, fieldProps);
	}

	@autobind
	protected handleModalSave(
		fieldProps: FieldRenderProps<FormIncomeSimpleData[]>,
		income: FormIncomeSimpleData,
		key?: number,
	): void {
		const {
			input: { value: incomeList = [] },
		} = fieldProps;
		const newIncomeList = [...incomeList];

		if (key !== undefined) {
			newIncomeList[key] = income;
		} else {
			newIncomeList.push(income);
		}
		this.setState({
			showModal: false,
			editKey: undefined,
		});

		this.handleChange(newIncomeList, fieldProps);
		this.handleBlur(undefined, fieldProps);
	}

	protected getTotalAmount(incomeList: FormIncomeSimpleData[]): number {
		return incomeList.reduce((totalAmount, incomeData) => totalAmount + incomeData.amount, 0);
	}

	protected getTotalReceiptAmount(incomeList: FormIncomeSimpleData[]): number {
		return incomeList.reduce((totalReceiptAmount, incomeData) => totalReceiptAmount + incomeData.receiptAmount, 0);
	}

	@autobind
	protected handleValidate(value: FormIncomeSimpleData[], allValues: Record<string, any>): string {
		const { required } = this.props;

		if (!this.isDisabled() && required && (!Array.isArray(value) || value.length === 0)) {
			return "A jövedelem megadása kötelező!";
		}

		return super.handleValidate(value, allValues);
	}

	protected isValidationShow(fieldProps: FieldRenderProps<FormIncomeSimpleData[]>): boolean {
		const { showModal } = this.state;

		if (showModal) {
			return false;
		}

		return super.isValidationShow(fieldProps);
	}

	public static validateIncomeList(incomeList: FormIncomeSimpleData[]): string | undefined {
		if (!incomeList?.length) {
			return "Legalább egy bevételi forrást meg kell adni!";
		}

		const totalAmount = incomeList.reduce((sum, income) => sum + (income.amount || 0), 0);

		if (totalAmount < Config.getMinWage()) {
			return `A jövedelem összege nem lehet kevesebb, mint az aktuális minimálbér (${formatText(
				Config.getMinWage(),
				"ft",
			)})!`;
		}

		return undefined;
	}
}
